import {useRecoilState, useRecoilValue} from "recoil";
import {rodinGenPipeline, rodinHistoryStatus} from "../../../../store";
import {useTips} from "../../../../common/GlobalTips";
import {rodinHistory} from "../../../../utils/net";
import {cropImage, fetchObjectToLocalURL} from "../../../../utils/format";
import style from "../rodingenerateboard.module.css";
import {useEffect, useRef, useState} from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

const History = () => {
    const genPipeline = useRecoilValue(rodinGenPipeline);
    const [historyStatus, setHistoryStatus] = useRecoilState(rodinHistoryStatus)
    const [historyLoading, setHistoryLoading] = useState(false)
    const tip = useTips();
    const historyRef = useRef(null);
    const [, setHoveredIndex] = useState(null);
    const handleCloseHistoryRef = useRef();
    const { t } = useTranslation();

    handleCloseHistoryRef.current = () => {
        setHistoryStatus({ ...historyStatus, historyList: [], showHistoryList: false, refImage: null });
    };

    const handleMouseEnter = (index) => {
        setHoveredIndex(index);
        const flatHistories = historyStatus.historyList
        setHistoryStatus({ ...historyStatus, previewHistory: flatHistories[index] })
    };

    const handleMouseLeave = () => {
        setHoveredIndex(null);
        setHistoryStatus({ ...historyStatus, previewHistory: false })
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (historyRef.current && !historyRef.current.contains(event.target)) {
                handleCloseHistoryRef.current();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    const handleCloseHistory = () => {
        // console.log('historyStatus',historyStatus)
        setHistoryStatus({ ...historyStatus, historyList: [], showHistoryList: false, refImage: null })
    }

    const handleHistoryClick = (historyItem) => {
        const flatHistories = historyStatus.historyList
        setHistoryStatus({ ...historyStatus, selectedHistory: flatHistories[historyItem], showHistoryList: false })
    }

    async function downloadImage(imageUrl) {
        return await fetchObjectToLocalURL(imageUrl);
    }

    const handlerOpenHistory = async () => {
        if (!historyStatus.showHistoryList) {
            // console.log('historyStatus',historyStatus)
            setHistoryLoading(true);
            const uuid = genPipeline.taskInfo.data;
            const res_history = await rodinHistory({ uuid });

            if (res_history?.data?.error === "INVALID_REQUEST") {
                tip({
                    type: 'primary',
                    content: t('TIP_PRIMARY_HISTORY_RECORDS'),
                });
                setHistoryLoading(false);
                return;
            }

            if (!genPipeline.geoConfirmed.status) {
                let flatHistories = [];

                for (const versionKey in res_history.data) {
                    const version = res_history.data[versionKey];
                    flatHistories.push({
                        preview_image: null,
                        sdf_image: version.preview_image,
                        settings: version.settings,
                        version: versionKey,
                    });
                }

                flatHistories.sort((a, b) => {
                    const versionANum = parseInt(a.version.substring(1));
                    const versionBNum = parseInt(b.version.substring(1));

                    return versionBNum - versionANum;
                });

                const downloadPromises = flatHistories.map(version =>
                    downloadImage(version.sdf_image).then(localUrl => {
                        version.sdf_image = localUrl;
                        return version;
                    })
                );

                try {
                    const downloadedVersions = await Promise.all(downloadPromises);

                    const cropPromises = downloadedVersions.map(version =>
                        cropImage(version.sdf_image, 360, 360, 4).then(croppedImage => {
                            version.preview_image = croppedImage
                            return version;
                        })
                    );

                    const finalVersions = await Promise.all(cropPromises);

                    setHistoryStatus({
                        ...historyStatus,
                        showHistoryList: true,
                        historyList: finalVersions
                    });
                    setTimeout(() => {
                        setHistoryLoading(false);
                    }, 100);

                } catch (error) {
                    Sentry.captureException(error)
                    tip({
                        type: 'error',
                        content: t('TIP_ERR_HISTORY_IMG') + error,
                    });
                }

            } else if (genPipeline.geoConfirmed.status && !genPipeline.mtlConfirmed.status) {
                let flatHistories = [];
                const fetchSettings = async (url) => {
                    try {
                        const response = await axios.get(url);
                        const settingsData = response.data;
                        const lastModified = response.headers['last-modified'];
                        return { settingsData, lastModified };
                    } catch (error) {
                        console.error('Error fetching settings: ', error);
                        return null;
                    }
                };

                const sortHistories = async () => {
                    const fetchPromises = flatHistories.map(item => fetchSettings(item.settings));

                    const results = await Promise.all(fetchPromises);
                    results.forEach((result, index) => {
                        if (result) {
                            flatHistories[index].settings = result.settingsData;
                            flatHistories[index].lastModified = new Date(result.lastModified).getTime();
                        }
                    });

                    flatHistories.sort((a, b) => b.lastModified - a.lastModified);
                    setHistoryStatus({ ...historyStatus, showHistoryList: true, historyList: flatHistories });
                    setHistoryLoading(false);
                };

                const refImageHistory = res_history.data;

                if (!refImageHistory) {
                    tip({
                        type: 'primary',
                        content: t('TIP_PRIMARY_HISTORY_RECORDS'),
                    });
                    setHistoryLoading(false);
                    handleCloseHistory();
                    return;
                }

                for (const mtlImageName in refImageHistory) {
                    const mtlImageVersions = refImageHistory[mtlImageName];
                    for (const versionKey in mtlImageVersions) {
                        const version = mtlImageVersions[versionKey];
                        const versionCount = Object.keys(mtlImageVersions).length;
                        flatHistories.push({
                            model: version.model,
                            texture_diffuse: version.texture_diffuse,
                            texture_pbr: version.texture_pbr,
                            texture_shaded: version.texture_shaded,
                            texture_normal: version.texture_normal,
                            preview_image: version.preview_image,
                            settings: version.settings,
                            ref_image: mtlImageName,
                            version: versionKey,
                            version_num: versionCount,
                        });
                    }
                }

                Promise.all(flatHistories.map(async (item) => {
                    try {
                        const responses = await Promise.all([
                            axios.get(item.preview_image, { responseType: 'blob' }),
                            // axios.get(item.texture_diffuse, { responseType: 'blob' }),
                            // axios.get(item.texture_pbr, { responseType: 'blob' }),
                            // axios.get(item.texture_shaded, { responseType: 'blob' })
                        ]);

                        return responses.map(response => URL.createObjectURL(response.data));
                    } catch (error) {
                        console.error('Error fetching resources: ', error);
                        return [
                            item.preview_image,
                            // item.texture_diffuse,
                            // item.texture_pbr,
                            // item.texture_shaded
                        ];
                    }
                })).then(localUrls => {
                    flatHistories.forEach((item, index) => {
                        item.preview_image = localUrls[index][0];
                        // item.texture_diffuse = localUrls[index][1];
                        // item.texture_pbr = localUrls[index][2];
                        // item.texture_shaded = localUrls[index][3];
                    });

                    sortHistories().catch(error => {
                        tip({
                            type: 'error',
                            content: t('TIP_ERR_HISTORY_DATA') + error,
                        });
                        setHistoryLoading(false);
                    });
                }).catch(error => {
                    tip({
                        type: 'error',
                        content: t('TIP_ERR_HISTORY_IMG_PRE') + error,
                    });
                    setHistoryLoading(false);
                });

            } else {
                tip({
                    type: 'primary',
                    content: t('TIP_PRIMARY_HISTORY_RECORDS'),
                });
            }
        } else {
            handleCloseHistory();
        }
    }
    return (
        <div ref={historyRef}>
            <div className={style.historyWrapper} id='history' onClick={handlerOpenHistory}>
                {!historyLoading ? (
                    <>
                        <div className={style.historyIcon}>
                            <span className={style.clock1}></span>
                            <span className={style.clock2}></span>
                        </div>
                        <div className="flex items-center space-x-2">
                            {t('HISTORY_TITLE')}
                        </div>
                    </>
                ) : (
                    <div
                        className="animate-spin h-4 w-4 border-2 border-gray-400 rounded-full border-t-transparent"></div>
                )}
            </div>
            <div className={`${style.historyListWrapper} ${historyStatus.showHistoryList ? style.show : ''}`}>
                <div className={style.historyHeader}>History</div>
                <div className={style.historyBody}>
                    {historyStatus.historyList.map((historyItem, index) => {
                        return (
                            <div
                                key={index}
                                className={`${style.historyItem} ${historyStatus.selectedHistory === historyItem ? style.selected : ''}`}
                                onClick={handleHistoryClick.bind(null, index)}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <img src={historyItem.preview_image} alt={`History ${index}`} className={style.historyImg} />
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export { History }